/**
 * File: contractDue.ts
 * Path: packages/config/src/contracts/contractDue.ts
 * Description: When each payment is due, and what is owed past its date.
 *
 * Each payment gets a plain date (YYYY-MM-DD). Where the plan allows it the
 * date is worked out from the day the client signed (the deposit is due that
 * day, month N is N-1 months later). Payments that depend on the work ("On
 * delivery", the end of a stage) have no worked-out date: staff set
 * it by hand, stored under contracts/{id}/dueDates/{payment reference}.
 * Receipts are not touched: a payment is owed until staff mark it received.
 *
 * Every amount is turned into whole cents first, so the money owed page, the
 * client page and the accounting section add up exactly the same.
 */

import type { PaymentPart } from "../references/references";
import { randToCents, printRand } from "../accounting/money";
import { fillTemplate, templateText, type MessageTemplateOverrides } from "../leads/messageTemplates";

import { DEFAULT_MONTHLY_DUE_DAY, monthlyDueDate, toMonthlyDueDay, type MonthlyDueDay } from "./contractDueDay";
import { contractBalance, type PaymentReceipt } from "./contractReceipts";
import type { Contract } from "./contractTypes";

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

export const isDueDate = (value: string): boolean =>
  DATE.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));

/** A day set by hand on one payment: contracts/{id}/dueDates/{reference}. */
export interface PaymentDueDate {
  reference: string;
  dueOn: string;
}

/** A due date from a stored document; null when it is not one. */
export const toPaymentDueDate = (
  id: string,
  data: Record<string, unknown> | null | undefined,
): PaymentDueDate | null => {
  const dueOn = typeof data?.dueOn === "string" ? data.dueOn : "";
  return isDueDate(dueOn) ? { reference: id, dueOn } : null;
};

/** The day in South Africa, as 2026-10-08. */
export const southAfricanDay = (ms: number): string =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Johannesburg" }).format(new Date(ms));

const dayNumber = (iso: string): number => Date.parse(`${iso}T00:00:00Z`) / DAY_MS;

/** The same day N months on, or the month's last day when it has no such day. */
export const addMonthsToDate = (date: string, months: number): string => {
  const [y, m, d] = date.split("-").map(Number);
  const index = y * 12 + (m - 1) + months;
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${year}-${pad(month)}-${pad(Math.min(d, lastDay))}`;
};

/** The day the client signed; money is owed from their signature, not the countersignature. */
export const contractSignedOn = (contract: Pick<Contract, "clientSignature" | "signedAt">): string | null => {
  const ms = contract.clientSignature?.signedAt ?? contract.signedAt;
  return typeof ms === "number" && ms > 0 ? southAfricanDay(ms) : null;
};

/**
 * Epoch ms of the day the client signed, for showing and ordering: their
 * signature, else an imported contract's recorded signing day (noon in South
 * Africa), else the stored signing time. Null when it was never signed.
 */
export const contractSignedAtMs = (contract: Pick<Contract, "clientSignature" | "signedAt" | "imported">): number | null => {
  const signature = contract.clientSignature?.signedAt;
  if (typeof signature === "number" && signature > 0) return signature;
  const recorded = contract.imported?.signedOn;
  if (recorded && isDueDate(recorded)) return Date.parse(`${recorded}T10:00:00Z`);
  return typeof contract.signedAt === "number" && contract.signedAt > 0 ? contract.signedAt : null;
};

/** The date a payment's place in the plan gives it, or null when staff must set it. */
export const workedOutDueOn = (
  part: PaymentPart,
  signedOn: string | null,
  dueDay: MonthlyDueDay = DEFAULT_MONTHLY_DUE_DAY,
): string | null => {
  if (!signedOn) return null;
  if (part.kind === "deposit") return signedOn;
  if (part.kind === "month") return monthlyDueDate(signedOn, part.n, dueDay);
  return null;
};

export type DueSource = "set" | "worked-out" | "none";

export interface OwedPayment {
  contractId: string;
  contractReference: string;
  clientId: string | null;
  clientName: string;
  clientPhone: string;
  /** "IDK-C012-02-DEP". */
  reference: string;
  label: string;
  /** Still owed on this payment, in whole cents. */
  owedCents: number;
  /** YYYY-MM-DD, or null while no date is known. */
  dueOn: string | null;
  dueSource: DueSource;
  /** Whole days past the due date; 0 when not late or no date. */
  daysLate: number;
}

const owesMoney = (contract: Pick<Contract, "status">): boolean =>
  contract.status === "client_signed" || contract.status === "signed";

/** What one signed contract still owes, payment by payment (paid ones left out). */
export const contractOwedPayments = (
  contract: Contract,
  receipts: readonly PaymentReceipt[],
  dueDates: readonly PaymentDueDate[],
  today: string,
): OwedPayment[] => {
  if (!owesMoney(contract)) return [];
  const byReference = new Map(dueDates.map((d) => [d.reference, d.dueOn]));
  const signedOn = contractSignedOn(contract);
  const { rows } = contractBalance(contract, [...receipts]);
  return rows.flatMap((row) => {
    const owedCents = randToCents(row.amount) - randToCents(row.receipt?.amount ?? 0);
    if (owedCents <= 0) return [];
    const set = byReference.get(row.reference);
    const worked = workedOutDueOn(row.part, signedOn, toMonthlyDueDay(contract.content.monthlyDueDay));
    const dueOn = set ?? worked;
    return [
      {
        contractId: contract.id,
        contractReference: contract.reference,
        clientId: contract.clientId,
        clientName: contract.content.client.name,
        clientPhone: contract.content.client.phone,
        reference: row.reference,
        label: row.label,
        owedCents,
        dueOn,
        dueSource: set ? "set" : worked ? "worked-out" : "none",
        daysLate: dueOn ? Math.max(0, dayNumber(today) - dayNumber(dueOn)) : 0,
      },
    ];
  });
};

export type AgeBucketKey = "0-30" | "31-60" | "61-90" | "90+";

export const AGE_BUCKETS: ReadonlyArray<{ key: AgeBucketKey; label: string }> = [
  { key: "0-30", label: "0 to 30 days late" },
  { key: "31-60", label: "31 to 60 days late" },
  { key: "61-90", label: "61 to 90 days late" },
  { key: "90+", label: "Over 90 days late" },
];

export const ageBucketFor = (daysLate: number): AgeBucketKey =>
  daysLate <= 30 ? "0-30" : daysLate <= 60 ? "31-60" : daysLate <= 90 ? "61-90" : "90+";

export interface MoneyOwed {
  /** Past due, oldest first inside each age group. */
  buckets: Array<{ key: AgeBucketKey; label: string; rows: OwedPayment[]; totalCents: number }>;
  overdueCents: number;
  /** Has a date, not reached yet. */
  comingCents: number;
  /** Owed, but no date known yet (set when delivered). */
  undated: OwedPayment[];
  undatedCents: number;
  /** Everything still owed. */
  totalCents: number;
}

/** Sorts every owed payment into the money owed page's groups. */
export const buildMoneyOwed = (payments: readonly OwedPayment[], today: string): MoneyOwed => {
  const late = payments.filter((p) => p.dueOn !== null && p.dueOn < today);
  const buckets = AGE_BUCKETS.map((b) => {
    const rows = late.filter((p) => ageBucketFor(p.daysLate) === b.key).sort((a, c) => c.daysLate - a.daysLate);
    return { ...b, rows, totalCents: rows.reduce((s, r) => s + r.owedCents, 0) };
  });
  const undated = payments.filter((p) => p.dueOn === null);
  const sum = (list: readonly OwedPayment[]) => list.reduce((s, r) => s + r.owedCents, 0);
  const coming = payments.filter((p) => p.dueOn !== null && p.dueOn >= today);
  return {
    buckets,
    overdueCents: sum(late),
    comingCents: sum(coming),
    undated,
    undatedCents: sum(undated),
    totalCents: sum(payments),
  };
};

/** The WhatsApp reminder, in plain words, for one late payment (Settings > Templates may change the wording). */
export const paymentReminderMessage = (p: OwedPayment, sender: string, overrides?: MessageTemplateOverrides): string =>
  fillTemplate(templateText("paymentReminder", overrides), {
    "first name": p.clientName.trim().split(/\s+/)[0] || "there",
    payment: p.label,
    amount: printRand(p.owedCents / 100),
    "due date": p.dueOn ?? "",
    reference: p.reference,
    sender,
  });
